import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { LoanHeader } from './components/LoanHeader';
import { StepIndicator } from './components/StepIndicator';
import { PrepareStep } from './components/PrepareStep';
import { GeneratingCard } from './components/GeneratingCard';
import { ReviewPackageStep } from './components/ReviewPackageStep';
import { DisclosureSummary } from './components/DisclosureSummary';
import { DisclosureHistory } from './components/DisclosureHistory';
import { EditRecipientModal } from './components/EditRecipientModal';
import { DocumentPreviewModal } from './components/DocumentPreviewModal';
import { SendDrawer } from './components/send/SendDrawer';
import { useDisclosureWorkflow } from './hooks/useDisclosureWorkflow';
import type { DisclosureDocument, IntegrationError, Recipient, SendOptions } from './types/disclosures';
export function App() {
  const workflow = useDisclosureWorkflow();
  const [editingRecipient, setEditingRecipient] = useState<Recipient | null>(null);
  const [previewDocument, setPreviewDocument] = useState<DisclosureDocument | null>(null);
  const [sendOpen, setSendOpen] = useState(false);
  const {
    activePair,
    activePackage,
    activeValidation,
    activeStages,
    activeError,
    currentStep
  } = workflow;
  const scrollToHistory = () => {
    document.getElementById('disclosure-history')?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };
  const handleErrorAction = (error: IntegrationError) => {
    if (error.kind === 'already_sent') {
      scrollToHistory();
      return;
    }
    if (error.kind === 'loan_locked') {
      workflow.generate(activePair.id);
      return;
    }
    workflow.retryValidation(activePair.id);
  };
  const handleSent = (options: SendOptions) => {
    workflow.completeSend(activePair.id, options);
  };
  const showGenerating = activePackage.status === 'preparing';
  const showPackage = activePackage.status === 'ready' || activePackage.status === 'sent';
  return <div className="min-h-screen w-full bg-slate-50" data-id="element-0" data-mp="356rh3pi3e8j">
      <LoanHeader status={activePackage.status} scenario={workflow.scenario} onScenarioChange={workflow.setScenario} data-id="element-1" data-mp="3ud1g7bihxsbo" />

      <main className="mx-auto w-full max-w-[1160px] px-6 py-6" data-id="element-2" data-mp="d45tw4joadn3">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-3" data-mp="85bbh3pmar3i">
          <div data-id="element-4" data-mp="3gle90gn0mze4">
            <StepIndicator currentStep={currentStep} completed={activePackage.status === 'sent'} data-id="element-5" data-mp="3gkv01pfcflc8" />

            <AnimatePresence mode="wait" data-id="element-6" data-mp="3bmthd5ltxo1a">
              <motion.div key={showGenerating ? 'generating' : showPackage ? 'package' : 'prepare'} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.2
            }} data-id="element-7" data-mp="3bnurn7v0u6hy">
                {showGenerating ? <GeneratingCard stages={activeStages} pairLabel={activePair.label} data-id="element-8" data-mp="2swbtpffutims" /> : showPackage ? <ReviewPackageStep pair={activePair} packageState={activePackage} error={activeError} onSend={() => setSendOpen(true)} onRegenerate={() => workflow.regenerate(activePair.id)} onToggleDocument={documentId => workflow.toggleDocument(activePair.id, documentId)} onPreview={setPreviewDocument} onErrorAction={handleErrorAction} data-id="element-9" data-mp="18czm8goc19lf" /> : <PrepareStep pairs={workflow.pairs} activePair={activePair} activePairId={workflow.activePairId} onSelectPair={workflow.setActivePairId} validation={workflow.validation} packages={workflow.packages} activeValidation={activeValidation} activeError={activeError} onGenerate={() => workflow.generate(activePair.id)} onGenerateAll={workflow.generateAll} onRetryValidation={() => workflow.retryValidation(activePair.id)} onEditRecipient={setEditingRecipient} onErrorAction={handleErrorAction} data-id="element-10" data-mp="1i2j5vojvd7hc" />}
              </motion.div>
            </AnimatePresence>

            <div className="mt-4" data-id="element-11" data-mp="14wx1qk7fn6yi">
              <DisclosureHistory entries={workflow.history} data-id="element-12" data-mp="14wnaktanitr8" />
            </div>
          </div>

          <DisclosureSummary pair={activePair} packageState={activePackage} data-id="element-13" data-mp="zx2c0jze8nf1" />
        </div>
      </main>

      {editingRecipient && <EditRecipientModal recipient={editingRecipient} onClose={() => setEditingRecipient(null)} onSave={recipient => {
      workflow.updateRecipient(activePair.id, recipient);
      setEditingRecipient(null);
    }} data-id="element-14" data-mp="vvp7cd81eq9p" />}

      {previewDocument && <DocumentPreviewModal document={previewDocument} onClose={() => setPreviewDocument(null)} data-id="element-15" data-mp="1w2c4pf29ka6q" />}

      <SendDrawer open={sendOpen} pair={activePair} documentCount={activePackage.selectedDocumentIds.length} onClose={() => setSendOpen(false)} onSent={handleSent} onViewHistory={() => {
      setSendOpen(false);
      scrollToHistory();
    }} data-id="element-16" data-mp="254ld9zdz1thh" />
    </div>;
}